import { CHECK_IN_MOOD_GROUP_KEY } from './schema';

/**
 * Card labels for the Home "Tracked" section's context-group cards.
 *
 * Every non-mood group the member recorded gets its own card, headed by
 * `checkin.contextGroup_<group>_summaryLabel`. The mood group is excluded: its
 * card is the Mood card, whose label and value come from elsewhere.
 *
 * This module never reads a catalog. Each caller owns its reader (the API's
 * catalog loader, the mobile translator) and passes a `lookup`, so the same
 * precedence runs on the server and on the device without either importing the
 * other's i18n layer.
 */

/** The catalog key, in the `checkin` namespace, that names a group's card. */
export function contextGroupSummaryLabelKey(group: string): string {
  return `contextGroup_${group}_summaryLabel`;
}

/**
 * The shape a group key must have before it is used to build a catalog key or
 * reported in telemetry. i18next reserves `.` and `:`, and a member-written key
 * is free text as far as the write path is concerned (`checkInContextSchema`
 * accepts any non-empty string), so anything outside this shape is never
 * looked up and never logged verbatim.
 */
const GROUP_KEY_SHAPE = /^[a-z0-9_-]{1,32}$/;

export interface LabelledContextGroup {
  group: string;
  tags: readonly string[];
  /** Absent only on the mood group. */
  label?: string;
}

export interface LabelledContextGroups {
  groups: LabelledContextGroup[];
  /** The keys of every non-mood group that took the fallback label, in order. */
  fallbacks: string[];
}

/**
 * Label each non-mood group: its own `label` when it arrived with one (a wire
 * label always wins), else the catalog's summary label via `lookup`, else
 * `fallbackLabel`. `lookup` returns `undefined` for a missing or empty key.
 *
 * The fallback (`home.checkinSummaryGroupFallbackLabel`) exists so a stored
 * group the catalog no longer names — a retired group still appended by
 * `orderedContextGroups`, or a key an old client wrote — keeps the member's
 * tags on screen under a heading. It is NOT how a served group gets its label:
 * `apps/api/src/check-ins/service/context-group-summary-labels.test.ts` fails
 * CI when any non-mood `GROUPS` key lacks a summary label, and that guard is
 * what keeps live groups off this path. A served group reaching it is a bug,
 * which is why every fallback is reported in `fallbacks` for the caller to log.
 */
export function labelContextGroups(
  groups: readonly { group: string; tags: readonly string[]; label?: string }[],
  lookup: (key: string) => string | undefined,
  fallbackLabel: string,
): LabelledContextGroups {
  const fallbacks: string[] = [];
  const labelled = groups.map((entry): LabelledContextGroup => {
    if (entry.group === CHECK_IN_MOOD_GROUP_KEY) return { group: entry.group, tags: entry.tags };
    if (entry.label !== undefined && entry.label.length > 0) {
      return { group: entry.group, tags: entry.tags, label: entry.label };
    }
    const resolved = GROUP_KEY_SHAPE.test(entry.group)
      ? lookup(contextGroupSummaryLabelKey(entry.group))
      : undefined;
    if (resolved !== undefined && resolved.length > 0) {
      return { group: entry.group, tags: entry.tags, label: resolved };
    }
    fallbacks.push(entry.group);
    return { group: entry.group, tags: entry.tags, label: fallbackLabel };
  });
  return { groups: labelled, fallbacks };
}

/**
 * The group key as a telemetry attribute: verbatim only when the caller's
 * catalog names the group (`checkin.contextGroup_<group>_label`), otherwise
 * `invalid`.
 *
 * The check-in write path accepts any group key, so a stored key can be text a
 * member wrote, and a shape check alone would let `drank_ten_beers` through to
 * an operational log (ADR-069 Rule 2). The catalog is shipped vocabulary, never
 * member text, and every served group is in it, so a served group missing its
 * card label still reports by name.
 */
export function telemetryGroupKey(
  group: string,
  lookup: (key: string) => string | undefined,
): string {
  if (!GROUP_KEY_SHAPE.test(group)) return 'invalid';
  return lookup(`contextGroup_${group}_label`) === undefined ? 'invalid' : group;
}
